// Top times for one (date, difficulty) of the daily puzzle. Mirrors
// the LeaderboardModal in CustomPuzzlePlay.tsx — same hm-leaderboard
// table styling — but wired to /solves/leaderboard instead of
// /custom/:id/leaderboard, and shows the viewer's rank in the footer.
// Entrypoints: WinModal "See today's leaderboard" button + rail Leaderboard chip.

import { useEffect, useState } from 'react';
import { FONT_MONO, v } from '../constants';
import type { Difficulty } from '../types';
import { fetchDailyLeaderboard, type DailyLeaderboardResponse } from '../lib/solves';
import { formatTime } from './modals';
import { useAuth } from '../auth/AuthContext';

const DIFFICULTIES: Difficulty[] = ['Easy', 'Medium', 'Hard', 'Expert'];

export function DailyLeaderboardModal({
  date,
  difficulty,
  onClose,
}: {
  date: string;
  /// Difficulty the modal opens with. The user can switch tabs once
  /// inside without closing — the underlying API endpoint is identical
  /// across difficulties.
  difficulty: Difficulty;
  onClose: () => void;
}) {
  const auth = useAuth();
  const [activeDifficulty, setActiveDifficulty] = useState<Difficulty>(difficulty);
  const [data, setData] = useState<DailyLeaderboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  // Scope tab — 'all' is the global top times; 'friends' filters to
  // viewer + everyone they follow (server-side). The Friends tab is
  // hidden for anonymous viewers since they have no follow graph.
  const [scope, setScope] = useState<'all' | 'friends'>('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setData(null);
    (async () => {
      const res = await fetchDailyLeaderboard(date, activeDifficulty, 25, scope);
      if (cancelled) return;
      setData(res);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [date, activeDifficulty, scope]);

  // ESC-to-close mirrors the shared Modal helper. We avoid re-using
  // Modal directly because we want to render a custom backdrop + table
  // structure (same as the custom-puzzle leaderboard) for visual
  // consistency.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const entries = data?.entries ?? [];

  return (
    <div className="hm-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true" aria-label="Daily leaderboard">
      <div className="hm-modal" onClick={e => e.stopPropagation()}>
        <button type="button" className="hm-modal-close" onClick={onClose} aria-label="Close">×</button>
        <h2>Today's leaderboard</h2>
        <p style={{ fontFamily: FONT_MONO, fontSize: 13, color: v('inkMute') }}>
          {date} · {activeDifficulty}
        </p>
        <div
          role="tablist"
          aria-label="Difficulty"
          style={{
            display: 'flex', gap: 4, marginTop: 10, padding: 2,
            border: `1px solid ${v('rule')}`, borderRadius: 999,
            background: v('paperDeep'), width: 'fit-content',
          }}
        >
          {DIFFICULTIES.map(d => {
            const active = d === activeDifficulty;
            return (
              <button
                key={d}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setActiveDifficulty(d)}
                style={{
                  background: active ? v('ink') : 'transparent',
                  color: active ? v('paper') : v('inkSoft'),
                  border: 'none', borderRadius: 999,
                  padding: '4px 12px', fontSize: 12,
                  fontWeight: active ? 600 : 400,
                  cursor: active ? 'default' : 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                {d}
              </button>
            );
          })}
        </div>
        {/* Scope tabs — only render the Friends pill for signed-in users
            (anonymous viewers can't have a follow graph). Tapping a tab
            re-fires the fetch with the new scope param. */}
        {auth.user && (
          <div style={{ display: 'flex', gap: 6, marginTop: 10, marginBottom: 4 }}>
            <button
              type="button"
              onClick={() => setScope('all')}
              className="hm-action"
              style={{
                padding: '4px 12px', fontSize: 12,
                ...(scope === 'all' ? { background: v('ink'), color: v('paper'), borderColor: v('ink') } : {}),
              }}
            >Global</button>
            <button
              type="button"
              onClick={() => setScope('friends')}
              className="hm-action"
              style={{
                padding: '4px 12px', fontSize: 12,
                ...(scope === 'friends' ? { background: v('ink'), color: v('paper'), borderColor: v('ink') } : {}),
              }}
            >Friends</button>
          </div>
        )}
        {loading ? (
          <p style={{ marginTop: 16, color: v('inkMute') }}>Loading…</p>
        ) : entries.length === 0 ? (
          <p style={{ marginTop: 16, color: v('inkMute') }}>
            {scope === 'friends'
              ? "None of your friends have posted a time yet. Invite a friend from your Account."
              : 'No times posted yet — be the first.'}
          </p>
        ) : (
          <ol className="hm-leaderboard">
            {entries.map((e, i) => (
              <li
                key={`${e.displayName}-${e.solvedAt}-${i}`}
                style={e.isMe ? { background: v('inkSoft'), color: v('paper'), borderRadius: 6 } : undefined}
              >
                <span className="hm-leaderboard-rank">{i + 1}</span>
                <span className="hm-leaderboard-name">{e.displayName}{e.isMe ? ' (you)' : ''}</span>
                <span className="hm-leaderboard-time">{formatTime(e.elapsedMs)}</span>
              </li>
            ))}
          </ol>
        )}
        {data && (data.viewerRank != null || data.totalSolves > 0) && (
          <p style={{ fontSize: 12, color: v('inkMute'), marginTop: 14 }}>
            {data.viewerRank != null
              ? <>You're <strong>#{data.viewerRank}</strong> of {data.totalSolves} solver{data.totalSolves === 1 ? '' : 's'}.</>
              : <>{data.totalSolves} solver{data.totalSolves === 1 ? '' : 's'} so far.</>}
          </p>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
          <button type="button" className="hm-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
